import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

const schema = yup.object().shape({
  serialNo: yup
    .string()
    .required("Serial Number is required")
    .min(3, "Serial Number must be at least 3 characters")
    .max(50, "Serial Number must not exceed 50 characters")
    .matches(/^[A-Za-z0-9]+$/, "Serial Number can only contain letters and numbers"),  
  model: yup
    .string()
    .required("Model is required")
    .oneOf(["Y1000K"], "Please select a valid model"),
  deviceName: yup
    .string()
    .max(50, "Device Name must not exceed 50 characters"),
});

function AddDeviceDialog({ open, onClose, onSuccess, device, isUpdateMode }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const api = useApi();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      serialNo: "",
      model: "",
      deviceName: ""
    }
  });

  // Set form values when in update mode
  useEffect(() => {
    if (isUpdateMode && device) {
      setValue("oldSerialNo", device.serialNo);
      setValue("serialNo", device.serialNo);
      setValue("model", device.model);
      setValue("deviceName", device.deviceName);
    }
  }, [isUpdateMode, device, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      let response;
      
      if (isUpdateMode) {
        // Update existing device
        response = await api.put("devices", data);
      } else {
        // Create new device
        response = await api.post("devices", data);
      }
      
      if (response.data.message) {
        toast.success(response.data.message);
        handleClose();
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 
                          `Failed to ${isUpdateMode ? 'update' : 'add'} device`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <MDTypography variant="h6">
          {isUpdateMode ? 'Update Device' : 'Add New Serial'}
        </MDTypography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <MDInput
                label="Serial Number"
                {...register("serialNo")}
                error={!!errors.serialNo}
                helperText={errors.serialNo?.message}
                fullWidth
                required
                // disabled={isUpdateMode} // Disable serial number field in update mode
              />
            </Grid>            
            <Grid item xs={12}>              <TextField
                  fullWidth
                  label="Model"
                  {...register("model")}
                  select
                  defaultValue={isUpdateMode && device ? device.model : ""}
                  // onChange={(e) => setValue("model", e.target.value)}
                  SelectProps={{ IconComponent: () => null }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '40px',
                    },
                    height: '40px',
                  }}
                  required
              >
                <MenuItem value="">Select a model</MenuItem>
                <MenuItem value="Y1000K">Y1000K</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label="Device Name"
                {...register("deviceName")}
                error={!!errors.deviceName}
                helperText={errors.deviceName?.message}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleClose}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            type="submit"
            color="info"
            variant="gradient"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              isUpdateMode ? "Update Device" : "Add Serial"
            )}
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

AddDeviceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  device: PropTypes.object,
  isUpdateMode: PropTypes.bool
};

AddDeviceDialog.defaultProps = {
  device: null,
  isUpdateMode: false
};

export default AddDeviceDialog;